import { Box, Typography } from "@mui/material";
import { ga, useIsDesktop, useUiLangState } from "./GaPrimitives";
import { GaDialogShell, Eyebrow, GaLimeCta, fillCopy } from "./GaDialogs";
import eggArt from "../../assets/img/myth-fact-egg.png";
import diamondIcon from "../../assets/img/2 MAIN SCREENS EN/Rewards/diamond-clear.png";
import minecoinIcon from "../../assets/img/2 MAIN SCREENS EN/Rewards/minecoin-clear.png";
import ucIcon from "../../assets/img/2 MAIN SCREENS EN/Rewards/uc-clear.png";

const noMotion = { "@media (prefers-reduced-motion: reduce)": { animation: "none" } };

const burstKeyframes = {
  "@keyframes eggShake": {
    "0%, 100%": { transform: "translateX(-50%) rotate(0deg)" },
    "25%": { transform: "translateX(calc(-50% - 3px)) rotate(-2deg)" },
    "75%": { transform: "translateX(calc(-50% + 3px)) rotate(2deg)" },
  },
  "@keyframes eggLidLeft": {
    "0%": { transform: "translate(0, 0) rotate(0deg)", opacity: 1 },
    "100%": { transform: "translate(-34px, -46px) rotate(-34deg)", opacity: 0 },
  },
  "@keyframes eggLidRight": {
    "0%": { transform: "translate(0, 0) rotate(0deg)", opacity: 1 },
    "100%": { transform: "translate(36px, -40px) rotate(30deg)", opacity: 0 },
  },
  "@keyframes eggBeam": {
    "0%": { transform: "translateX(-50%) scaleY(0)", opacity: 0 },
    "100%": { transform: "translateX(-50%) scaleY(1)", opacity: 1 },
  },
  "@keyframes beamGlow": {
    "0%, 100%": { filter: "brightness(1)" },
    "50%": { filter: "brightness(1.25)" },
  },
  "@keyframes eggRim": {
    "0%": { opacity: 0 },
    "100%": { opacity: 1 },
  },
  "@keyframes rewardOut": {
    "0%": {
      left: "50%",
      top: "70%",
      opacity: 0,
      transform: "translate(-50%, -50%) scale(0.15) rotate(0deg)",
    },
    "100%": {
      left: "var(--x)",
      top: "var(--y)",
      opacity: 1,
      transform: "translate(-50%, -50%) scale(1) rotate(var(--rot))",
    },
  },
  "@keyframes rewardFloat": {
    "0%, 100%": { transform: "translateY(0)" },
    "50%": { transform: "translateY(-6px)" },
  },
};

function EggHalf({ clip, animation }) {
  return (
    <Box
      component="img"
      src={eggArt}
      alt=""
      sx={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        objectFit: "contain",
        clipPath: clip,
        ...(animation ? { animation, ...noMotion } : null),
      }}
    />
  );
}

function BurstReward({ src, alt, size, x, y, rot, delay, children }) {
  return (
    <Box
      sx={{
        position: "absolute",
        left: x,
        top: y,
        width: size,
        height: size,
        zIndex: 4,
        "--x": x,
        "--y": y,
        "--rot": rot,
        animation: `rewardOut 0.9s cubic-bezier(0.16, 0.84, 0.24, 1.12) ${delay} both`,
        ...noMotion,
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          animation: "rewardFloat 2.8s ease-in-out 1.15s infinite",
          ...noMotion,
        }}
      >
        {src ? (
          <Box
            component="img"
            src={src}
            alt={alt || ""}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              display: "block",
              filter: "drop-shadow(0 6px 8px rgba(0,0,0,0.4))",
            }}
          />
        ) : (
          children
        )}
      </Box>
    </Box>
  );
}

function CollectedEggBurst({ open }) {
  const isDesktop = useIsDesktop();
  const eggW = isDesktop ? 112 : 100;
  const eggH = isDesktop ? 156 : 140;

  if (!open) {
    return <Box aria-hidden sx={{ width: "100%", height: isDesktop ? 228 : 208, mb: isDesktop ? 2 : 1.5 }} />;
  }

  return (
    <Box
      aria-label="Easter egg collected"
      sx={{
        position: "relative",
        width: "100%",
        height: isDesktop ? 228 : 208,
        mb: isDesktop ? 2 : 1.5,
        overflow: "visible",
        pointerEvents: "none",
        background:
          "radial-gradient(ellipse at 50% 72%, rgba(255, 196, 70, 0.16), transparent 58%)",
        ...burstKeyframes,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          bottom: eggH * 0.46,
          width: isDesktop ? 54 : 46,
          height: isDesktop ? 150 : 128,
          transformOrigin: "bottom center",
          background:
            "linear-gradient(to top, rgba(255, 244, 210, 0.95) 0%, rgba(255, 196, 72, 0.62) 26%, rgba(255, 220, 140, 0.16) 68%, transparent 100%)",
          animation: "eggBeam 0.6s cubic-bezier(0.2, 0.85, 0.2, 1) 0.28s both, beamGlow 1.8s ease-in-out 0.95s infinite",
          zIndex: 1,
          ...noMotion,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          bottom: eggH * 0.46,
          width: 12,
          height: isDesktop ? 136 : 116,
          transformOrigin: "bottom center",
          background:
            "linear-gradient(to top, rgba(255,255,245,0.95), rgba(255,220,140,0.2) 70%, transparent)",
          animation: "eggBeam 0.55s cubic-bezier(0.2, 0.85, 0.2, 1) 0.32s both",
          zIndex: 1,
          ...noMotion,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          left: "50%",
          bottom: 0,
          width: eggW,
          height: eggH,
          transform: "translateX(-50%)",
          animation: "eggShake 0.42s ease-in-out",
          zIndex: 2,
          ...noMotion,
        }}
      >
        <EggHalf clip="polygon(0 50%, 15% 44%, 32% 53%, 50% 46%, 68% 54%, 84% 45%, 100% 51%, 100% 100%, 0 100%)" />
        <EggHalf
          clip="polygon(0 0, 50% 0, 50% 46%, 32% 53%, 15% 44%, 0 50%)"
          animation="eggLidLeft 0.55s cubic-bezier(0.2, 0.7, 0.2, 1) 0.32s both"
        />
        <EggHalf
          clip="polygon(50% 0, 100% 0, 100% 51%, 84% 45%, 68% 54%, 50% 46%)"
          animation="eggLidRight 0.55s cubic-bezier(0.2, 0.7, 0.2, 1) 0.32s both"
        />
        <Box
          sx={{
            position: "absolute",
            left: "14%",
            right: "14%",
            top: "45%",
            height: 14,
            borderRadius: "50%",
            background: "radial-gradient(ellipse, rgba(255, 236, 180, 0.95), transparent 70%)",
            opacity: 0,
            animation: "eggRim 0.35s ease 0.34s both",
          }}
        />
      </Box>

      <BurstReward src={diamondIcon} alt="Diamond" size={isDesktop ? 42 : 36} x="14%" y="18%" rot="-18deg" delay="0.4s" />
      <BurstReward src={minecoinIcon} alt="Minecoin" size={isDesktop ? 40 : 34} x="50%" y="8%" rot="6deg" delay="0.34s" />
      <BurstReward src={ucIcon} alt="UC" size={isDesktop ? 48 : 42} x="82%" y="16%" rot="16deg" delay="0.46s" />
      <BurstReward size={isDesktop ? 22 : 18} x="6%" y="42%" rot="-24deg" delay="0.5s">
        <Box
          sx={{
            width: "100%",
            height: "78%",
            mt: "12%",
            borderRadius: "46% 42% 50% 40%",
            background: "radial-gradient(circle at 32% 30%, #ffe7a6, #e2a028 58%, #8d5a10)",
            boxShadow: "0 0 10px rgba(255, 190, 60, 0.7)",
          }}
        />
      </BurstReward>
      <BurstReward size={26} x="90%" y="48%" rot="28deg" delay="0.44s">
        <Box
          component="img"
          src={eggArt}
          alt=""
          sx={{ width: "100%", height: "100%", objectFit: "cover", clipPath: "inset(6% 42% 52% 6%)", display: "block" }}
        />
      </BurstReward>
      {[
        { x: "30%", y: "30%", d: "0.38s", c: "#fff6d2" },
        { x: "66%", y: "26%", d: "0.46s", c: "#ffd56a" },
        { x: "40%", y: "8%", d: "0.52s", c: "#fff" },
        { x: "24%", y: "52%", d: "0.5s", c: "#ffe09a" },
      ].map((spark) => (
        <BurstReward key={spark.x + spark.y} size={7} x={spark.x} y={spark.y} rot="0deg" delay={spark.d}>
          <Box sx={{ width: "100%", height: "100%", borderRadius: "50%", bgcolor: spark.c, boxShadow: `0 0 8px ${spark.c}` }} />
        </BurstReward>
      ))}
    </Box>
  );
}

export function EggCollectedDialog({
  open,
  bonusLabel,
  title = "",
  rewardEggs = 0,
  onClose,
}) {
  const { t } = useUiLangState();
  const isDesktop = useIsDesktop();
  const d = t.dialogs;
  const amount = Math.max(0, Number(rewardEggs) || 0);
  const amountLabel =
    amount > 0 ? fillCopy(d.eggsEarned, { n: amount }) : bonusLabel || "";
  const rewardTitle = title ? String(title) : "";

  return (
    <GaDialogShell open={open} onClose={onClose} width={320} desktopWidth={520}>
      {isDesktop ? (
        <>
          <Eyebrow>{d.collectedChip}</Eyebrow>
          <CollectedEggBurst open={open} />
          {rewardTitle ? (
            <Typography
              sx={{
                fontFamily: "'Rajdhani', sans-serif",
                fontWeight: 700,
                fontSize: 22,
                letterSpacing: "0.4px",
                color: ga.white,
                mb: 0.75,
                maxWidth: 420,
              }}
            >
              {rewardTitle}
            </Typography>
          ) : null}
          {amountLabel ? (
            <Typography
              sx={{
                fontFamily: "'Rajdhani', sans-serif",
                fontWeight: 700,
                fontSize: 32,
                color: ga.lime,
                mb: 3,
              }}
            >
              {amountLabel}
            </Typography>
          ) : (
            <Typography
              sx={{
                fontFamily: "'Rajdhani', sans-serif",
                fontWeight: 700,
                fontSize: 32,
                color: ga.gold,
                mb: 3,
              }}
            >
              {bonusLabel || d.plusOne}
            </Typography>
          )}
        </>
      ) : (
        <>
          <CollectedEggBurst open={open} />
          <Box sx={{ display: "flex", gap: 0.75, mb: 0.75 }}>
            <Typography
              sx={{
                fontFamily: "'Rajdhani', sans-serif",
                fontWeight: 700,
                fontSize: 22,
                letterSpacing: "1.5px",
                color: ga.white,
              }}
            >
              {d.eggCollected}
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Rajdhani', sans-serif",
                fontWeight: 700,
                fontSize: 22,
                letterSpacing: "1.5px",
                color: ga.lime,
              }}
            >
              {d.eggCollectedAccent}
            </Typography>
          </Box>
          {rewardTitle ? (
            <Typography
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 700,
                fontSize: 14,
                color: ga.white,
                mb: 0.5,
                maxWidth: 260,
              }}
            >
              {rewardTitle}
            </Typography>
          ) : null}
          {amountLabel ? (
            <Typography
              sx={{
                fontFamily: "'Rajdhani', sans-serif",
                fontWeight: 700,
                fontSize: 26,
                letterSpacing: "0.6px",
                color: ga.lime,
                mb: 2,
              }}
            >
              {amountLabel}
            </Typography>
          ) : null}
        </>
      )}
      <GaLimeCta onClick={onClose}>{d.awesome}</GaLimeCta>
    </GaDialogShell>
  );
}
